// Evaluador de LOGROS del modo carrera (client-side). Dado el estado de una carrera, devuelve
// qué logros de UNA carrera están satisfechos (con el club/temporada con el que se consiguieron)
// y los "eventos de país" para los logros cross-carrera (ganar una copa con cada país de
// Sudamérica, que se acumulan en el backend entre todas las partidas del usuario).
//
// La detección es idempotente: se puede correr en cada avance. El backend guarda solo el PRIMER
// desbloqueo (con su fecha). Ver logrosDefs.js para el catálogo y backend/models/Achievement.js.

import { PAISES_CONMEBOL, PAISES_UEFA, GRANDES_LIGAS } from './logrosDefs';
import { SELECCIONES_BY_PAIS } from './selecciones';

const CUP_TIPOS = ['champions', 'europa', 'libertadores', 'sudamericana'];
// Ranking de "hasta dónde llegó" en un Mundial, para comparar fases.
const MUNDIAL_RANK = { campeon: 7, final: 6, semis: 5, cuartos: 4, octavos: 3, '16avos': 2, grupos: 1, no_clasif: 0 };

// Normaliza los títulos de la carrera a una lista rica: { tipo, club_nombre, club_logo,
// temporada, pais, estrellas, invicto, liga_equipos }. Fuente principal: career.titulos_detalle
// (lo llena careerTurn al cerrar cada temporada). Fallback para saves viejos: historial_stats
// (sin país/estrellas/invicto → los logros que dependen de esos datos no se evalúan).
function titulosDeCarrera(career) {
    if (Array.isArray(career.titulos_detalle) && career.titulos_detalle.length > 0) {
        return career.titulos_detalle;
    }
    const out = [];
    for (const s of (career.historial_stats || [])) {
        for (const t of (s.titulos || [])) {
            let tipo = 'liga';
            if (t === 'Campeón Champions League') tipo = 'champions';
            else if (t === 'Campeón Europa League') tipo = 'europa';
            else if (t === 'Campeón Copa Libertadores') tipo = 'libertadores';
            else if (t === 'Campeón Copa Sudamericana') tipo = 'sudamericana';
            out.push({
                tipo,
                club_nombre: s.club_nombre || '',
                club_logo: s.club_logo || '',
                temporada: s.temporada,
                pais: undefined,
                estrellas: undefined,
                invicto: undefined,
                liga_equipos: undefined,
            });
        }
    }
    return out;
}

function totalGoles(career) {
    const hist = (career.historial_stats || []).reduce((acc, s) => acc + (s.goles || 0), 0);
    return hist + (career.stats_temporada?.goles || 0);
}

function totalAsist(career) {
    const hist = (career.historial_stats || []).reduce((acc, s) => acc + (s.asistencias || 0), 0);
    return hist + (career.stats_temporada?.asistencias || 0);
}

function clubCtx(career, titulo) {
    return {
        club_nombre: titulo?.club_nombre || career.club_actual?.nombre || '',
        club_logo: titulo?.club_logo || career.club_actual?.logo || '',
        temporada: titulo?.temporada ?? career.temporada,
    };
}

// Devuelve { unlocked: [{key, club_nombre, club_logo, temporada}], paisEvents: [...] }.
// ctx.retired = true cuando el jugador se acaba de retirar (para logros que solo se confirman al
// colgar los botines, como "toda la carrera en el mismo club").
export function evaluarLogros(career, ctx = {}) {
    if (!career) return { unlocked: [], paisEvents: [] };

    const titulos = titulosDeCarrera(career);
    const ligas = titulos.filter(t => t.tipo === 'liga');
    const cups = titulos.filter(t => CUP_TIPOS.includes(t.tipo));
    const track = career.logros_track || {};
    const atributos = career.atributos || {};
    const goles = totalGoles(career);
    const asist = totalAsist(career);
    const media = Math.round(career.rating || 0);
    // Puntos de atributos aplicados en la carrera = suma de los atributos (empiezan en 0 y solo
    // suben, así que el total invertido es su suma).
    const puntosAtrib = Object.values(atributos).reduce((a, v) => a + (v || 0), 0);

    const unlocked = [];
    const add = (key, titulo) => unlocked.push({ key, ...clubCtx(career, titulo) });

    // ───────── FÁCIL ─────────
    if (ligas.length >= 5) add('facil_5_ligas', ligas[4]);
    const champ = titulos.find(t => t.tipo === 'champions');
    if (champ) add('facil_champions', champ);
    const eur = titulos.find(t => t.tipo === 'europa');
    if (eur) add('facil_europa', eur);
    const sud = titulos.find(t => t.tipo === 'sudamericana');
    if (sud) add('facil_sudamericana', sud);
    const lib = titulos.find(t => t.tipo === 'libertadores');
    if (lib) add('facil_libertadores', lib);

    // Jugar en las 5 grandes ligas (1ª división) en una misma carrera.
    const ligasJugadas = track.ligas_jugadas || [];
    const grandesD1 = new Set(
        ligasJugadas.filter(l => l.division === 1 && GRANDES_LIGAS.includes(l.pais)).map(l => l.pais)
    );
    if (GRANDES_LIGAS.every(p => grandesD1.has(p))) add('facil_5_grandes', null);

    if (media >= 85) add('facil_media_85', null);

    // ───────── MEDIO ─────────
    const ligaClubs = new Set(ligas.map(t => t.club_nombre));
    if (ligas.length >= 5 && ligaClubs.size >= 5) add('medio_5_ligas_5_equipos', ligas[ligas.length - 1]);

    const cupClubs = new Set(cups.map(t => t.club_nombre));
    if (cups.length >= 3 && cupClubs.size >= 3) add('medio_3_intl_3_equipos', cups[cups.length - 1]);

    // 5 ligas con el mismo equipo: algún club con >=5 títulos de liga.
    const ligaPorClub = {};
    for (const t of ligas) ligaPorClub[t.club_nombre] = (ligaPorClub[t.club_nombre] || 0) + 1;
    const mismoClub = Object.entries(ligaPorClub).find(([, n]) => n >= 5);
    if (mismoClub) add('medio_5_ligas_mismo', ligas.find(t => t.club_nombre === mismoClub[0]));

    if (track.remonto_0_3) add('medio_remontada_0_3', null);

    if ((track.clubs_5est || []).length >= 3) add('medio_3_clubs_5est', null);

    if (puntosAtrib >= 45) add('medio_45_atrib', null);

    if (goles > 750) add('medio_750_goles', null);

    if (media >= 90) add('medio_media_90', null);

    // Doblete (copa + liga local) en una MISMA temporada. Agrupamos los títulos del jugador por
    // temporada y buscamos una con liga + la copa correspondiente.
    const tiposPorTemporada = {};
    for (const t of titulos) {
        (tiposPorTemporada[t.temporada] = tiposPorTemporada[t.temporada] || new Set()).add(t.tipo);
    }
    const dobleteLiber = Object.entries(tiposPorTemporada).find(([, s]) => s.has('liga') && s.has('libertadores'));
    if (dobleteLiber) add('medio_liber_liga', titulos.find(t => String(t.temporada) === dobleteLiber[0] && t.tipo === 'liga'));
    const dobleteChamp = Object.entries(tiposPorTemporada).find(([, s]) => s.has('liga') && s.has('champions'));
    if (dobleteChamp) add('medio_champ_liga', titulos.find(t => String(t.temporada) === dobleteChamp[0] && t.tipo === 'liga'));

    // Toda la carrera en el mismo club: dificil con cualquier club, imposible si además ese club
    // tiene >=3 estrellas al retirarte. Ambos exigen retiro POR EDAD (no el voluntario).
    const clubsDistintos = new Set((career.historial_clubes || []).map(c => c.tm_id));
    const unClub = clubsDistintos.size === 1 && (career.historial_clubes || []).length >= 1;
    if (unClub && ctx.retiredByAge) add('dificil_un_club', null);
    if (unClub && ctx.retiredByAge && (career.club_actual?.estrellas || 0) >= 3) {
        add('imposible_un_club_3est', null);
    }

    // ───────── DIFÍCIL ─────────
    if (goles > 900) add('dificil_900_goles', null);
    if (asist > 750) add('dificil_750_asist', null);
    if (asist > 900) add('imposible_900_asist', null);

    if (puntosAtrib >= 50) add('dificil_50_atrib', null);

    const sudam2 = cups.find(t => t.tipo === 'sudamericana' && t.estrellas != null && t.estrellas <= 2);
    if (sudam2) add('dificil_sudam_2est', sudam2);

    const europa25 = cups.find(t => t.tipo === 'europa' && t.estrellas != null && t.estrellas <= 2.5);
    if (europa25) add('dificil_europa_25est', europa25);

    const invicto = cups.find(t => t.invicto === true);
    if (invicto) add('dificil_intl_invicto', invicto);

    if (media >= 95) add('dificil_media_95', null);

    // ───────── IMPOSIBLE ─────────
    const lib2 = cups.find(t => t.tipo === 'libertadores' && t.estrellas != null && t.estrellas <= 2);
    if (lib2) add('imposible_libertadores_2est', lib2);

    const champ25 = cups.find(t => t.tipo === 'champions' && t.estrellas != null && t.estrellas <= 2.5);
    if (champ25) add('imposible_champions_25est', champ25);

    const ligaInvicto18 = ligas.find(t => t.invicto === true && (t.liga_equipos || 0) >= 18 && t.division === 1);
    if (ligaInvicto18) add('imposible_liga_invicto_18', ligaInvicto18);

    if (media >= 99) add('imposible_media_99', null);

    // ───────── SELECCIÓN + MUNDIAL ─────────
    // IMPORTANTE: todos los logros requieren participación REAL del jugador (que haya sido
    // convocado), no que su selección haya jugado. `jugador_jugo` = convocado en el Mundial;
    // `jugador_convocado_ciclo` = convocado en algún momento del ciclo (clasif o Mundial);
    // los goles (goles/goles_ko/goles_final) ya son 0 si el jugador no fue convocado.
    const selCarrera = career.seleccion_carrera || {};
    const mundiales = career.mundiales_historicos || [];
    const selGoles = selCarrera.goles || 0;
    const estrellasSel = (m) => SELECCIONES_BY_PAIS[m.pais_jugador]?.estrellas ?? 9;
    const mundialesJugados = mundiales.filter(m => m.jugador_jugo);
    const mundialesCampeon = mundialesJugados.filter(m => m.llego_a === 'campeon');
    // Clasificó a un Mundial siendo parte de la selección (convocado en el ciclo) y su país entró.
    const clasificoConSel = (m) => m.jugador_convocado_ciclo && (MUNDIAL_RANK[m.llego_a] || 0) >= 1;
    const mejorGolesMundial = mundiales.reduce((mx, m) => Math.max(mx, m.goles || 0), 0);
    const selClub = { club_nombre: career.nacionalidad || '', club_logo: '', temporada: career.temporada };
    const addSel = (key) => unlocked.push({ key, ...selClub });

    // Fácil
    if ((selCarrera.partidos || 0) > 0) addSel('sel_facil_convocado');
    if (selGoles >= 25) addSel('sel_facil_5_goles');
    if (mundiales.some(clasificoConSel)) addSel('sel_facil_clasificar');

    // Medio
    if (mundialesJugados.some(m => (MUNDIAL_RANK[m.llego_a] || 0) >= MUNDIAL_RANK.semis)) addSel('sel_medio_semifinal');
    if (mundiales.some(m => (m.goles_ko || 0) > 0)) addSel('sel_medio_gol_ko');
    if (mundiales.some(m => clasificoConSel(m) && estrellasSel(m) <= 1.5)) addSel('sel_medio_clasif_15est');
    if (mundialesJugados.some(m => (MUNDIAL_RANK[m.llego_a] || 0) >= MUNDIAL_RANK.final && estrellasSel(m) <= 3.5)) addSel('sel_medio_final_35est');
    if (selGoles > 50) addSel('sel_medio_25_goles');

    // Difícil
    if (mundialesCampeon.length > 0) addSel('sel_dificil_campeon');
    if (mundiales.some(m => (m.goles_final || 0) > 0)) addSel('sel_dificil_gol_final');
    if (mundialesJugados.length >= 5) addSel('sel_dificil_5_mundiales');
    if (mundiales.some(m => (m.goles || 0) > 8)) addSel('sel_dificil_8_goles_mun');
    if (selGoles > 75) addSel('sel_dificil_50_goles');

    // Imposible
    if (mundialesCampeon.some(m => estrellasSel(m) <= 2)) addSel('sel_imp_campeon_2est');
    if (mundiales.some(m => (m.goles || 0) >= 13)) addSel('sel_imp_12_goles_mun');
    if (mundialesCampeon.length >= 3) addSel('sel_imp_3_mundiales_gan');
    if (selGoles > 100) addSel('sel_imp_75_goles');

    // ───────── EVENTOS DE PAÍS (cross-carrera) ─────────
    // Cada copa CONMEBOL ganada aporta su país al acumulador del backend; cuando se completan
    // todos los países de PAISES_CONMEBOL se desbloquea el logro correspondiente (en el backend).
    // Cada Champions / Europa ganada aporta su país europeo al acumulador (logros "con cada país
    // de Europa"). Idem Libertadores / Sudamericana con los países de CONMEBOL.
    const paisEvents = [];
    for (const t of cups) {
        const esConmebol = (t.tipo === 'libertadores' || t.tipo === 'sudamericana') && t.pais && PAISES_CONMEBOL.includes(t.pais);
        const esUefa = (t.tipo === 'champions' || t.tipo === 'europa') && t.pais && PAISES_UEFA.includes(t.pais);
        if (esConmebol || esUefa) {
            paisEvents.push({
                tipo: t.tipo,
                pais: t.pais,
                club_nombre: t.club_nombre || '',
                club_logo: t.club_logo || '',
                temporada: t.temporada,
            });
        }
    }

    // ───────── PROGRESO (de la carrera ACTUAL) ─────────
    // Para mostrar el avance de cada logro de una carrera. Se recalcula desde el estado actual, así
    // que se "reinicia" solo cuando empezás una carrera nueva (sus títulos/goles/media vuelven a 0).
    // Los logros cross-carrera (copas con cada país) llevan su progreso aparte (acumulador backend).
    const maxLigaMismo = Object.values(ligaPorClub).reduce((m, n) => Math.max(m, n), 0);
    const has = (x) => (x ? 1 : 0);
    const progreso = {
        facil_5_ligas: { cur: ligas.length, target: 5 },
        facil_champions: { cur: has(champ), target: 1 },
        facil_europa: { cur: has(eur), target: 1 },
        facil_sudamericana: { cur: has(sud), target: 1 },
        facil_libertadores: { cur: has(lib), target: 1 },
        facil_5_grandes: { cur: grandesD1.size, target: 5 },
        facil_media_85: { cur: media, target: 85 },
        medio_5_ligas_5_equipos: { cur: ligaClubs.size, target: 5 },
        medio_3_intl_3_equipos: { cur: cupClubs.size, target: 3 },
        medio_5_ligas_mismo: { cur: maxLigaMismo, target: 5 },
        medio_remontada_0_3: { cur: has(track.remonto_0_3), target: 1 },
        medio_3_clubs_5est: { cur: (track.clubs_5est || []).length, target: 3 },
        medio_45_atrib: { cur: puntosAtrib, target: 45 },
        medio_750_goles: { cur: goles, target: 750 },
        medio_media_90: { cur: media, target: 90 },
        medio_liber_liga: { cur: has(dobleteLiber), target: 1 },
        medio_champ_liga: { cur: has(dobleteChamp), target: 1 },
        dificil_900_goles: { cur: goles, target: 900 },
        dificil_750_asist: { cur: asist, target: 750 },
        imposible_900_asist: { cur: asist, target: 900 },
        dificil_50_atrib: { cur: puntosAtrib, target: 50 },
        dificil_sudam_2est: { cur: has(sudam2), target: 1 },
        dificil_europa_25est: { cur: has(europa25), target: 1 },
        dificil_intl_invicto: { cur: has(invicto), target: 1 },
        dificil_media_95: { cur: media, target: 95 },
        dificil_un_club: { texto: clubsDistintos.size <= 1 ? '1 club (se confirma al retirarte por edad)' : `${clubsDistintos.size} clubes` },
        imposible_un_club_3est: {
            texto: clubsDistintos.size <= 1
                ? `1 club · ${career.club_actual?.estrellas || 0}★ ${(career.club_actual?.estrellas || 0) >= 3 ? '(necesita ≥3★ al retirarte por edad)' : '(necesita ≥3★)'}`
                : `${clubsDistintos.size} clubes`,
        },
        imposible_libertadores_2est: { cur: has(lib2), target: 1 },
        imposible_champions_25est: { cur: has(champ25), target: 1 },
        imposible_liga_invicto_18: { cur: has(ligaInvicto18), target: 1 },
        imposible_media_99: { cur: media, target: 99 },
        // Selección + Mundial
        sel_facil_convocado: { cur: has((selCarrera.partidos || 0) > 0), target: 1 },
        sel_facil_5_goles: { cur: Math.min(selGoles, 25), target: 25 },
        sel_facil_clasificar: { cur: has(mundiales.some(clasificoConSel)), target: 1 },
        sel_medio_semifinal: { cur: has(mundialesJugados.some(m => (MUNDIAL_RANK[m.llego_a] || 0) >= MUNDIAL_RANK.semis)), target: 1 },
        sel_medio_gol_ko: { cur: has(mundiales.some(m => (m.goles_ko || 0) > 0)), target: 1 },
        sel_medio_clasif_15est: { cur: has(mundiales.some(m => clasificoConSel(m) && estrellasSel(m) <= 1.5)), target: 1 },
        sel_medio_final_35est: { cur: has(mundialesJugados.some(m => (MUNDIAL_RANK[m.llego_a] || 0) >= MUNDIAL_RANK.final && estrellasSel(m) <= 3.5)), target: 1 },
        sel_medio_25_goles: { cur: Math.min(selGoles, 50), target: 50 },
        sel_dificil_campeon: { cur: has(mundialesCampeon.length > 0), target: 1 },
        sel_dificil_gol_final: { cur: has(mundiales.some(m => (m.goles_final || 0) > 0)), target: 1 },
        sel_dificil_5_mundiales: { cur: mundialesJugados.length, target: 5 },
        sel_dificil_8_goles_mun: { cur: Math.min(mejorGolesMundial, 8), target: 8 },
        sel_dificil_50_goles: { cur: Math.min(selGoles, 75), target: 75 },
        sel_imp_campeon_2est: { cur: has(mundialesCampeon.some(m => estrellasSel(m) <= 2)), target: 1 },
        sel_imp_12_goles_mun: { cur: Math.min(mejorGolesMundial, 13), target: 13 },
        sel_imp_3_mundiales_gan: { cur: mundialesCampeon.length, target: 3 },
        sel_imp_75_goles: { cur: Math.min(selGoles, 100), target: 100 },
    };

    return { unlocked, paisEvents, progreso };
}
